import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CompanyAdmin, getCompanyAdmins, isUserLastPrimaryAdmin } from 'core'

import { Col, Icon, Row, Skeleton, Table, Tooltip } from 'common/components'
import { useLoginUserV2, useQueryRequestV2 } from 'common/hooks'

import AddAdminForm from './AddAdminForm'
import LastPrimaryAdminModal from './LastPrimaryAdminModal'
import { confirmRemoveAdmin } from './removeAdmin'

import './Admins.less'

const Admins = () => {
  const { t: translate } = useTranslation()
  const { user } = useLoginUserV2()

  const [userToRemove, setUserToRemove] = useState<CompanyAdmin | null>(null)
  const [lastPrimaryAdminModalIsVisible, setLastPrimaryAdminModalIsVisible] = useState(false)

  const {
    data: admins,
    isLoading,
    reload: reloadAdmins,
  } = useQueryRequestV2({ initialState: [], queryFn: () => getCompanyAdmins() })

  const { sendQuery: isUserLastPrimaryAdminQuery } = useQueryRequestV2({ initialState: false })

  const adminsColumns = useMemo(
    () => [
      {
        dataIndex: 'name',
        key: 'name',
        sorter: false,
        render: (_, record: any) => (
          <div className="flex align-center" style={{ gap: '10px' }}>
            <Icon className="fad fa-user-circle fa-2x" type="primary" />
            <span>{record.name}</span>
          </div>
        ),
      },
      {
        key: 'action',
        render: (_, record: any) => (
          <div className="action_column">
            {admins.length < 2 || record.id === user.companyUserId ? (
              <Tooltip
                title={translate('admin-dashboard.company-settings.company-admins.remove-admin-tooltip')}
                position="left"
              >
                <a className="remove_action_disabled">
                  {translate('admin-dashboard.company-settings.company-admins.remove-admin-button')}
                </a>
              </Tooltip>
            ) : (
              <a
                className="remove_action"
                onClick={async () =>
                  (await isUserLastPrimaryAdminQuery(() => isUserLastPrimaryAdmin(record.id)))
                    ? (setLastPrimaryAdminModalIsVisible(true), setUserToRemove(record))
                    : await confirmRemoveAdmin(translate, record, reloadAdmins)
                }
              >
                {translate('admin-dashboard.company-settings.company-admins.remove-admin-button')}
              </a>
            )}
          </div>
        ),
      },
    ],
    [admins],
  )

  return (
    <>
      <Row>
        <Col span={24}>
          <Skeleton active loading={isLoading}>
            <Table
              className="admins-table"
              dataSource={admins}
              loading={isLoading}
              columns={adminsColumns}
              pagination={false}
            />
            <AddAdminForm translate={translate} onAddAdmin={reloadAdmins} />
          </Skeleton>
        </Col>
      </Row>
      <LastPrimaryAdminModal
        visible={lastPrimaryAdminModalIsVisible}
        userToRemove={userToRemove}
        onAddAdmin={reloadAdmins}
        onCancel={() => setLastPrimaryAdminModalIsVisible(false)}
      />
    </>
  )
}

export default Admins
